import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAcademicYears,
  createAcademicYear,
  updateAcademicYear,
  deleteAcademicYear,
  getAcademicYearById,
} from '../services/academicYearService';
import Notification from '../components/Common/Notification';

export const AcademicYearContext = createContext();

export const AcademicYearProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllAcademicYears = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAcademicYears(authHeaders);
      setAcademicYears(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching academic years.';
      showNotification(errorMessage, true);
    }
  };

  const fetchAcademicYearById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAcademicYearById(id, authHeaders);
      setSelectedAcademicYear(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching academic year with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addAcademicYear = async (academicYearData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newAcademicYear = await createAcademicYear(academicYearData, authHeaders);
      setAcademicYears(prevAcademicYears => [...prevAcademicYears, newAcademicYear]);
      showNotification('Academic year added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding academic year.';
      showNotification(errorMessage, true);
    }
  };

  const editAcademicYear = async (id, academicYearData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedAcademicYear = await updateAcademicYear(id, academicYearData, authHeaders);
      setAcademicYears(prevAcademicYears =>
        prevAcademicYears.map(year => (year.id === id ? updatedAcademicYear : year))
      );
      showNotification('Academic year updated successfully!', false);
      return updatedAcademicYear;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating academic year.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeAcademicYear = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteAcademicYear(id, authHeaders);
      setAcademicYears(prevAcademicYears => prevAcademicYears.filter(year => year.id !== id));
      showNotification('Academic year removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing academic year.';
      showNotification(errorMessage, true);
    }
  };


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <AcademicYearContext.Provider
      value={{
        academicYears,
        selectedAcademicYear,
        fetchAllAcademicYears,
        fetchAcademicYearById,
        addAcademicYear,
        editAcademicYear,
        removeAcademicYear,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </AcademicYearContext.Provider>
  );
};

export const useAcademicYear = () => useContext(AcademicYearContext);
